import { useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  type AgentConfigBody,
  type ExpertSummary,
  type LiveQuestion,
  type QaItem,
  type QaStatus,
  type QaVote,
} from "../api";
import { ExpertMark } from "./design/ExpertMark";
import { Icon } from "./design/Icon";

const LEAVE_MS = 320;

type Tab = "queue" | "chat";

/// Tabbed Q&A surface. Per-meeting agent is single, so the design's
/// per-agent header chip is flattened into a single top-left identity
/// row. Voted/answered items animate out of the queue (height → 0)
/// and reappear in the chat-of-record tab; dismissed items just go.
export function ApprovalQueue({
  meetingId,
  items,
  experts,
  liveQuestions = [],
  agent,
  onItemsChange,
}: {
  meetingId: string;
  items: QaItem[];
  experts: ExpertSummary[];
  /** Underlying live_questions, keyed by id to qa_items (1:1 via
   *  qa_projection). Used to surface inline answers on the queue card
   *  and trigger the leave-animation when answer_text becomes set. */
  liveQuestions?: LiveQuestion[];
  /** Resolved agent. The queue identity row pulls expert + archetype
   *  from this — channel is shown read-only since it's a pre-meeting
   *  decision. */
  agent: AgentConfigBody;
  onItemsChange: (next: QaItem[]) => void;
}) {
  const expert =
    experts.find((e) => e.id === agent.expert_id) ?? experts[0] ?? null;
  const archetypeName =
    expert?.archetypes?.find((a) => a.id === agent.archetype_id)
      ?.display_name ?? null;
  const readOnly = agent.proactiveness === "notes-only";
  const liveById = new Map(liveQuestions.map((q) => [q.id, q]));

  // Animate items leaving the queue. An item leaves when it transitions
  // from `pending` to anything else OR when its underlying live_question
  // gets `answer_text` populated. We track ids in `leavingIds` and keep
  // rendering them in the queue for LEAVE_MS so the height-collapse
  // CSS transition can run; afterward React unmounts them and they
  // reappear in the chat-of-record tab via the items prop.
  const [leavingIds, setLeavingIds] = useState<Set<string>>(new Set());
  const prevItemsRef = useRef<QaItem[]>(items);
  const prevAnsweredRef = useRef<Set<string>>(
    new Set(liveQuestions.filter((q) => q.answer_text).map((q) => q.id)),
  );

  useEffect(() => {
    const prevById = new Map(prevItemsRef.current.map((q) => [q.id, q]));
    const newlyAnswered = liveQuestions
      .filter(
        (q) => q.answer_text && !prevAnsweredRef.current.has(q.id),
      )
      .map((q) => q.id);
    const newlyResolved = items
      .filter((q) => {
        const prev = prevById.get(q.id);
        return prev && prev.status === "pending" && q.status !== "pending";
      })
      .map((q) => q.id);
    const transitioning = new Set<string>([
      ...newlyResolved,
      ...newlyAnswered,
    ]);
    prevItemsRef.current = items;
    prevAnsweredRef.current = new Set(
      liveQuestions.filter((q) => q.answer_text).map((q) => q.id),
    );
    if (transitioning.size === 0) return;
    setLeavingIds((prev) => {
      const next = new Set(prev);
      transitioning.forEach((id) => next.add(id));
      return next;
    });
    const t = setTimeout(() => {
      setLeavingIds((prev) => {
        const next = new Set(prev);
        transitioning.forEach((id) => next.delete(id));
        return next;
      });
    }, LEAVE_MS);
    return () => clearTimeout(t);
  }, [items, liveQuestions]);

  const [tab, setTab] = useState<Tab>("queue");

  // Queue tab shows pending items + anything mid-leave so the
  // animation has frames to play. Anything dismissed/resolved that's
  // past the leave window only shows in the chat-of-record tab.
  const queueItems = items.filter(
    (q) => q.status === "pending" || leavingIds.has(q.id),
  );
  const chatItems = items.filter(
    (q) =>
      (q.status === "posted" || q.status === "spoken") &&
      !leavingIds.has(q.id),
  );
  const queuedCount = items.filter((q) => q.status === "pending").length;
  const channelBanner = (() => {
    if (agent.proactiveness === "notes-only") {
      return "Items capture silently. Nothing reaches the meeting.";
    }
    return agent.channel === "voice"
      ? "Bot speaks aloud on your thumbs-up."
      : "Posts to Meet chat on your thumbs-up.";
  })();

  return (
    <section
      style={{
        background: "var(--card)",
        border: "1px solid var(--rule)",
        borderRadius: "var(--r-3)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <header
        style={{
          padding: "var(--space-3) var(--space-4)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          flexWrap: "wrap",
          borderBottom: "1px solid var(--rule)",
        }}
      >
        {expert ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <ExpertMark expert={expert} size={28} />
            <div
              style={{
                display: "grid",
                gap: 2,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  className="mono"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: `var(--${expert.color_token}-deep)`,
                  }}
                >
                  [{expert.short_name}]
                </span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {archetypeName ?? expert.display_name}
                </span>
              </div>
              <div
                className="dim"
                style={{ fontSize: 11.5, color: "var(--ink-3)" }}
              >
                {channelBanner}
              </div>
            </div>
          </div>
        ) : null}
        <div
          role="tablist"
          aria-label="Q&A surface"
          style={{
            display: "inline-flex",
            padding: 2,
            gap: 2,
            borderRadius: 7,
            background: "var(--paper-3)",
            border: "1px solid var(--rule)",
          }}
        >
          <TabPill
            on={tab === "queue"}
            onClick={() => setTab("queue")}
            icon="qa"
            label="Approval queue"
            count={queuedCount}
          />
          <TabPill
            on={tab === "chat"}
            onClick={() => setTab("chat")}
            icon="chat"
            label="Chat of record"
            count={chatItems.length}
          />
        </div>
      </header>

      {tab === "queue" ? (
        <QueueTab
          meetingId={meetingId}
          items={queueItems}
          allItems={items}
          liveById={liveById}
          agent={agent}
          leavingIds={leavingIds}
          readOnly={readOnly}
          onItemsChange={onItemsChange}
        />
      ) : (
        <ChatTab items={chatItems} liveById={liveById} expert={expert} />
      )}
    </section>
  );
}

function QueueTab({
  meetingId,
  items,
  allItems,
  liveById,
  agent,
  leavingIds,
  readOnly,
  onItemsChange,
}: {
  meetingId: string;
  items: QaItem[];
  /** Full items array (not just queue-visible) so optimistic vote can
   *  produce the next full state for `onItemsChange`. */
  allItems: QaItem[];
  liveById: Map<string, LiveQuestion>;
  agent: AgentConfigBody;
  leavingIds: Set<string>;
  readOnly: boolean;
  onItemsChange: (next: QaItem[]) => void;
}) {
  if (items.length === 0) {
    return (
      <div
        style={{
          padding: "var(--space-4)",
          color: "var(--muted)",
          font: "var(--type-body-sm)",
        }}
      >
        No questions yet. The expert will surface high-leverage questions
        here as the meeting unfolds.
      </div>
    );
  }
  return (
    <ul
      style={{
        listStyle: "none",
        padding: "var(--space-3)",
        margin: 0,
        display: "grid",
        gap: "var(--space-2)",
      }}
    >
      {items.map((item) => (
        <QaRow
          key={item.id}
          item={item}
          live={liveById.get(item.id)}
          leaving={leavingIds.has(item.id)}
          readOnly={readOnly}
          onVote={async (vote) => {
            if (readOnly) return;
            const snapshot = allItems;
            const optimistic = allItems.map((q) =>
              q.id === item.id
                ? {
                    ...q,
                    vote,
                    status: optimisticStatus(vote, agent),
                  }
                : q,
            );
            onItemsChange(optimistic);
            try {
              const resp = await api.voteQaItem(meetingId, item.id, vote);
              onItemsChange(
                optimistic.map((q) =>
                  q.id === item.id
                    ? { ...q, vote: resp.vote, status: resp.status }
                    : q,
                ),
              );
            } catch (e) {
              onItemsChange(snapshot);
              const msg =
                e instanceof ApiError
                  ? e.message
                  : e instanceof Error
                    ? e.message
                    : String(e);
              console.error("vote failed:", msg);
            }
          }}
        />
      ))}
    </ul>
  );
}

function ChatTab({
  items,
  liveById,
  expert,
}: {
  items: QaItem[];
  liveById: Map<string, LiveQuestion>;
  expert: ExpertSummary | null;
}) {
  if (items.length === 0) {
    return (
      <div
        style={{
          padding: "var(--space-4)",
          color: "var(--muted)",
          font: "var(--type-body-sm)",
        }}
      >
        Nothing in the chat of record yet. Approved questions land here
        after a thumbs-up.
      </div>
    );
  }
  return (
    <ol
      style={{
        listStyle: "none",
        padding: "var(--space-3)",
        margin: 0,
        display: "grid",
        gap: "var(--space-2)",
      }}
    >
      {items.map((item) => (
        <ChatPost
          key={item.id}
          item={item}
          live={liveById.get(item.id)}
          expert={expert}
        />
      ))}
    </ol>
  );
}

function TabPill({
  on,
  onClick,
  icon,
  label,
  count,
}: {
  on: boolean;
  onClick: () => void;
  icon: "qa" | "chat";
  label: string;
  count?: number;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 11px",
        borderRadius: 5,
        cursor: "pointer",
        border: "none",
        background: on ? "var(--card)" : "transparent",
        color: on ? "var(--ink)" : "var(--ink-3)",
        boxShadow: on ? "var(--sh-1)" : "none",
        fontFamily: "inherit",
        fontSize: 11.5,
        fontWeight: on ? 600 : 500,
      }}
    >
      <Icon name={icon} size={11} />
      {label}
      {typeof count === "number" && (
        <span
          className="mono"
          style={{ fontSize: 10.5, color: "var(--ink-3)", fontWeight: 500 }}
        >
          · {count}
        </span>
      )}
    </button>
  );
}

function QaRow({
  item,
  live,
  leaving,
  onVote,
  readOnly,
}: {
  item: QaItem;
  live: LiveQuestion | undefined;
  leaving: boolean;
  onVote: (v: QaVote) => Promise<void>;
  readOnly?: boolean;
}) {
  const [busy, setBusy] = useState<QaVote | null>(null);
  const dismissed = item.status === "dismissed";
  const acted = item.status === "posted" || item.status === "spoken";
  const answer = live?.answer_text ?? null;

  async function vote(v: QaVote) {
    if (busy || dismissed) return;
    setBusy(v);
    try {
      await onVote(v);
    } finally {
      setBusy(null);
    }
  }

  return (
    <li
      data-leaving={leaving ? "true" : undefined}
      style={{
        display: "grid",
        gridTemplateRows: leaving ? "0fr" : "1fr",
        opacity: leaving ? 0 : 1,
        transition: `grid-template-rows ${LEAVE_MS}ms ease, opacity ${Math.round(LEAVE_MS * 0.75)}ms ease`,
      }}
    >
      <div style={{ overflow: "hidden", minHeight: 0 }}>
        <div
          style={{
            padding: "var(--space-3)",
            border: "1px solid var(--rule)",
            borderRadius: "var(--radius-md)",
            background: "var(--paper-raised)",
            opacity: dismissed ? 0.55 : 1,
            transition: "opacity .2s",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              className="mono dim"
              style={{
                fontSize: 10.5,
                marginLeft: "auto",
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              proposed
            </span>
          </div>
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              textDecoration: dismissed ? "line-through" : "none",
            }}
          >
            {item.text}
          </div>
          {item.rationale && (
            <div
              className="dim"
              style={{
                fontSize: 11.5,
                marginTop: 5,
                fontStyle: "italic",
              }}
            >
              {item.rationale}
            </div>
          )}
          {answer && (
            <div
              style={{
                marginTop: "var(--space-2)",
                paddingTop: "var(--space-2)",
                borderTop: "1px dashed var(--rule)",
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: "var(--ok)",
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                  fontWeight: 600,
                }}
              >
                ↳ answer ({live?.answer_source ?? "transcript"})
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {answer}
              </div>
            </div>
          )}
          <div
            style={{
              marginTop: 9,
              display: "flex",
              gap: 6,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {!readOnly && (
              <>
                <ThumbButton
                  dir="up"
                  active={item.vote === "up"}
                  disabled={dismissed || busy !== null || acted}
                  onClick={() => vote("up")}
                />
                <ThumbButton
                  dir="down"
                  active={item.vote === "down"}
                  disabled={dismissed || busy !== null}
                  onClick={() => vote("down")}
                />
              </>
            )}
            <StatusPill status={item.status} />
          </div>
        </div>
      </div>
    </li>
  );
}

function ChatPost({
  item,
  live,
  expert,
}: {
  item: QaItem;
  live: LiveQuestion | undefined;
  expert: ExpertSummary | null;
}) {
  const c = expert?.color_token ?? "muted";
  const answer = live?.answer_text ?? null;
  const sentLabel = item.status === "spoken" ? "Asked aloud" : "Posted to chat";
  return (
    <li
      style={{
        padding: 10,
        borderRadius: 8,
        background: "var(--card)",
        border: "1px solid var(--rule)",
        borderLeft: `3px solid var(--${c})`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
        }}
      >
        {expert && (
          <span
            className="mono"
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: `var(--${c}-deep)`,
            }}
          >
            [{expert.short_name}]
          </span>
        )}
        <span
          className="mono dim"
          style={{
            fontSize: 10.5,
            marginLeft: "auto",
            textTransform: "uppercase",
            letterSpacing: ".06em",
            color: "var(--ok)",
          }}
        >
          · {sentLabel}
        </span>
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{item.text}</div>
      {answer && (
        <div
          style={{
            marginTop: "var(--space-2)",
            paddingTop: "var(--space-2)",
            borderTop: "1px dashed var(--rule)",
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              color: "var(--ok)",
              textTransform: "uppercase",
              letterSpacing: ".06em",
              fontWeight: 600,
            }}
          >
            ↳ answer ({live?.answer_source ?? "transcript"})
          </div>
          <div style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            {answer}
          </div>
        </div>
      )}
    </li>
  );
}

function ThumbButton({
  dir,
  active,
  disabled,
  onClick,
}: {
  dir: "up" | "down";
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const up = dir === "up";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={up ? "Approve and post" : "Dismiss"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 9px",
        borderRadius: 6,
        cursor: disabled ? "default" : "pointer",
        fontFamily: "inherit",
        fontSize: 11,
        background: active ? "var(--ink)" : "var(--card)",
        color: active ? "var(--paper)" : "var(--ink-2)",
        border: `1px solid ${active ? "var(--ink)" : "var(--rule)"}`,
        opacity: disabled && !active ? 0.5 : 1,
        transition: "background .15s, color .15s, border-color .15s",
      }}
    >
      <ThumbIcon up={up} size={12} />
    </button>
  );
}

function ThumbIcon({ up, size = 12 }: { up: boolean; size?: number }) {
  // Filled thumb glyph. Existing design/Icon uses stroke; thumbs read
  // better as a solid shape, so this paths a filled SVG locally rather
  // than extending the shared stroke set.
  const path = up
    ? "M2 9V14H4V9H2ZM6 9V13.2C6 13.6 6.3 14 6.7 14H11.3C11.7 14 12.1 13.7 12.3 13.3L14 9.2V8C14 7.4 13.6 7 13 7H9.3L9.8 4.6V4.5C9.8 4.3 9.7 4.1 9.6 4L9 3.5L5.3 7.2C5.1 7.4 5 7.7 5 8V9"
    : "M14 7V2H12V7H14ZM10 7V2.8C10 2.4 9.7 2 9.3 2H4.7C4.3 2 3.9 2.3 3.7 2.7L2 6.8V8C2 8.6 2.4 9 3 9H6.7L6.2 11.4V11.5C6.2 11.7 6.3 11.9 6.4 12L7 12.5L10.7 8.8C10.9 8.6 11 8.3 11 8V7";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

function StatusPill({ status }: { status: QaStatus }) {
  const label: Record<QaStatus, string> = {
    pending: "Pending",
    posted: "Posted to chat",
    spoken: "Asked aloud",
    dismissed: "Dismissed",
  };
  const color: Record<QaStatus, string> = {
    pending: "var(--ink-3)",
    posted: "var(--ok)",
    spoken: "var(--ok)",
    dismissed: "var(--ink-3)",
  };
  if (status === "pending") return null;
  return (
    <span
      className="mono"
      style={{
        marginLeft: "auto",
        fontSize: 10.5,
        fontWeight: 600,
        color: color[status],
        textTransform: "uppercase",
        letterSpacing: ".06em",
      }}
    >
      · {label[status]}
    </span>
  );
}

function optimisticStatus(vote: QaVote, agent: AgentConfigBody): QaStatus {
  if (vote === "down") return "dismissed";
  if (vote === "up") {
    if (agent.proactiveness === "notes-only") return "pending";
    return agent.channel === "voice" ? "spoken" : "posted";
  }
  return "pending";
}
